import { ErrorPanel, LinearProgress } from "@octopusdeploy/design-system-components";
import type { LoaderQueryState } from "@octopusdeploy/octopus-react-client";
import { APIOperationStatusProvider, useLoaderQuery } from "@octopusdeploy/octopus-react-client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import type { ActiveWorkflowLoadingState } from "~/workflows/ActiveWorkflowLoadingState";
import { ActiveWorkflowLoadingStateProvider } from "~/workflows/ActiveWorkflowLoadingState";
import { DrawerOutPortalProvider, useDrawerNode } from "~/workflows/useDrawerNode";
import type { DialogWorkflowRegistration, DrawerWorkflowRegistration, WorkflowRegistration } from "~/workflows/workflowRegistrations";
import { isDialogWorkflowRegistration } from "~/workflows/workflowRegistrations";
interface ActiveWorkflowProps<Input, Output, LoaderData> {
    workflowRegistration: WorkflowRegistration<Input, Output, LoaderData>;
    input: Input;
    onWorkflowCompleted: (output: Output) => void;
    workflowInstanceId: string;
}
export function ActiveWorkflow<Input, Output, LoaderData>({ workflowRegistration, input, onWorkflowCompleted, workflowInstanceId }: ActiveWorkflowProps<Input, Output, LoaderData>) {
    const correlationContext = useMemo(() => ({
        "workflow.id": workflowRegistration.workflow.workflowId,
        "workflow.instance.id": workflowInstanceId,
    }), [workflowInstanceId, workflowRegistration.workflow.workflowId]);
    const queryState = useLoaderQuery<LoaderData>(async (repository) => {
        if (workflowRegistration.loader === undefined) {
            // If a workflow doesn't have a loader, it has no need for a loader.
            // Instead of trying to make the types more complex to avoid the need to pass loaderData here, it is much easier to always assume there is a loader and loader data
            // But then get consumers to ignore this value (because it will be inferred as `unknown`)
            // This is captured by the below type assertion - at runtime the value provided is simply undefined.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return undefined as unknown as LoaderData;
        }
        return await workflowRegistration.loader(repository, { input });
    }, correlationContext, `Workflow load: ${workflowRegistration.workflow.workflowId}`);
    return (<APIOperationStatusProvider correlationContext={correlationContext}>
            {isDialogWorkflowRegistration(workflowRegistration) ? (<ActiveDialogWorkflow workflowRegistration={workflowRegistration} input={input} onWorkflowCompleted={onWorkflowCompleted} loaderQueryState={queryState}/>) : (<ActiveDrawerWorkflow workflowRegistration={workflowRegistration} input={input} onWorkflowCompleted={onWorkflowCompleted} loaderQueryState={queryState}/>)}
        </APIOperationStatusProvider>);
}
interface ActiveDialogWorkflowProps<Input, Output, LoaderData> {
    workflowRegistration: DialogWorkflowRegistration<Input, Output, LoaderData>;
    input: Input;
    onWorkflowCompleted: (output: Output) => void;
    loaderQueryState: LoaderQueryState<LoaderData>;
}
function ActiveDialogWorkflow<Input, Output, LoaderData>({ workflowRegistration, input, onWorkflowCompleted, loaderQueryState }: ActiveDialogWorkflowProps<Input, Output, LoaderData>) {
    const { openDialog, isOpen, closeAllDialogs } = useDialogTrigger();
    // Required to open the dialog when the workflow is first started
    // We should change this abstraction to avoid this awkward openDialog call
    useEffect(() => {
        openDialog();
    }, [openDialog]);
    const completeWorkflow = useCallback((output: Output) => {
        closeAllDialogs();
        onWorkflowCompleted(output);
    }, [closeAllDialogs, onWorkflowCompleted]);
    const activeWorkflowLoadingState = useMemo<ActiveWorkflowLoadingState>(() => ({
        isLoading: loaderQueryState.isLoading,
        loadingError: loaderQueryState.error,
    }), [loaderQueryState.error, loaderQueryState.isLoading]);
    return (<ActiveWorkflowLoadingStateProvider activeWorkflowLoadingState={activeWorkflowLoadingState}>
            <Dialog open={isOpen}>{workflowRegistration.render({ input, completeWorkflow, loaderData: loaderQueryState.result })}</Dialog>
        </ActiveWorkflowLoadingStateProvider>);
}
interface ActiveDrawerWorkflowProps<Input, Output, LoaderData> {
    workflowRegistration: DrawerWorkflowRegistration<Input, Output, LoaderData>;
    input: Input;
    onWorkflowCompleted: (output: Output) => void;
    loaderQueryState: LoaderQueryState<LoaderData>;
}
function ActiveDrawerWorkflow<Input, Output, LoaderData>({ workflowRegistration, input, onWorkflowCompleted, loaderQueryState }: ActiveDrawerWorkflowProps<Input, Output, LoaderData>) {
    const [isOpen, setIsOpen] = useState(true);
    const completeWorkflow = useCallback((output: Output) => {
        setIsOpen(false);
        onWorkflowCompleted(output);
    }, [onWorkflowCompleted]);
    const { DrawerInPortal, DrawerOutPortal } = useDrawerNode();
    function getContent() {
        if (loaderQueryState.isLoading) {
            return (<DrawerOutPortal>
                    <LinearProgress variant={"indeterminate"} show={true}/>
                </DrawerOutPortal>);
        }
        else if (loaderQueryState.error !== null) {
            return (<DrawerOutPortal>
                    <ErrorPanel error={createErrorInfoFromError(loaderQueryState.error)}/>
                </DrawerOutPortal>);
        }
        else {
            return <DrawerOutPortalProvider drawerOutPortal={DrawerOutPortal}>{workflowRegistration.render({ input, completeWorkflow, loaderData: loaderQueryState.result })}</DrawerOutPortalProvider>;
        }
    }
    return (<>
            <DrawerInPortal variant="default" layout="none" isOpen={isOpen} onClose={() => setIsOpen(false)} title={workflowRegistration.loadingTitle} onPrimaryButtonClick={() => { }} primaryButtonLabel={workflowRegistration.primaryButtonLabel} isPrimaryButtonDisabled={true}>
                <></>
            </DrawerInPortal>
            {getContent()}
        </>);
}
